/**
 * @file     ReturningVisitorBanner.tsx
 * @purpose  Top-of-funnel passive sign-in entry for returning visitors: one
 *           line and Send a sign-in link on the panel radius, expanding to the
 *           email field. Posts the email to /api/auth/resend-magic-link, which
 *           never reveals whether an account exists (no enumeration). Hidden
 *           when the user is already authenticated; the funnel route guard
 *           handles that case with a redirect to /dashboard.
 */
'use client';

import { useState, type FormEvent } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Collapse from '@mui/material/Collapse';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { secureFetch } from '@/services/anti-scraping/secureFetch';
import { DEAL_FINDER_SUBMITTED_DESTINATION } from '@/core/config/ctaState';
import { hairline, hubColors } from '@/components/hub/hubKitStyles';
import { layoutTokens } from '@/design-system/tokens/layout';
import { fontWeightTokens } from '@/design-system/tokens/typography';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Status = 'idle' | 'submitting' | 'sent' | 'error' | 'throttled';

export const ReturningVisitorBanner = () => {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  if (isLoading || isAuthenticated) return null;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!EMAIL_RE.test(trimmed)) {
      setStatus('error');
      setErrorMessage(t('dealFinder.returning.invalidEmail', { defaultValue: 'Enter a valid email.' }));
      return;
    }
    setStatus('submitting');
    setErrorMessage(null);
    try {
      const res = await secureFetch('/api/auth/resend-magic-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed, next: DEAL_FINDER_SUBMITTED_DESTINATION }),
      });
      if (res.status === 429) {
        setStatus('throttled');
        return;
      }
      if (!res.ok) {
        setStatus('error');
        setErrorMessage(t('dealFinder.returning.failed', { defaultValue: 'That did not go through. Try again.' }));
        return;
      }
      setStatus('sent');
    } catch {
      setStatus('error');
      setErrorMessage(t('dealFinder.returning.failed', { defaultValue: 'That did not go through. Try again.' }));
    }
  };

  const sendLabel = t('dealFinder.returning.send', { defaultValue: 'Send a sign-in link' });
  const buttonSx = {
    minHeight: 44,
    px: 4,
    borderRadius: `${layoutTokens.radius.md}px`,
    textTransform: 'none',
    fontSize: 15,
    fontWeight: fontWeightTokens.medium,
    flexShrink: 0,
    whiteSpace: 'nowrap',
  } as const;

  return (
    <Box
      component="section"
      aria-label={t('dealFinder.returning.label', { defaultValue: 'Returning visitor sign-in' })}
      sx={{
        mb: { xs: 5, md: 6 },
        px: { xs: 4, sm: 5 },
        py: 3,
        borderRadius: `${layoutTokens.radius.lg}px`,
        border: hairline,
        backgroundColor: hubColors.surfaceCard,
      }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center', justifyContent: 'space-between' }}>
        <Box component="p" sx={{ m: 0, fontSize: 15, lineHeight: 1.45, color: hubColors.textSecondary, flex: '1 1 220px' }}>
          {t('dealFinder.returning.line', { defaultValue: 'Get a sign-in link to pick up your shortlist.' })}
        </Box>
        {!expanded && (
          <Button
            type="button"
            variant="outlined"
            onClick={() => setExpanded(true)}
            sx={{ ...buttonSx, borderColor: hubColors.borderStrong, color: hubColors.textPrimary }}
          >
            {sendLabel}
          </Button>
        )}
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}
        >
          <TextField
            type="email"
            inputMode="email"
            autoComplete="email"
            autoFocus
            label={t('dealFinder.returning.email', { defaultValue: 'Email' })}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (status === 'error') setStatus('idle');
            }}
            size="small"
            disabled={status === 'submitting' || status === 'sent'}
            error={status === 'error'}
            helperText={status === 'error' ? errorMessage : undefined}
            sx={{ flex: '1 1 220px', minWidth: 200 }}
          />
          <Button
            type="submit"
            variant="contained"
            disableElevation
            disabled={status === 'submitting' || status === 'sent'}
            sx={{ ...buttonSx, minHeight: 40 }}
          >
            {status === 'submitting' ? t('dealFinder.returning.sending', { defaultValue: 'Sending' }) : sendLabel}
          </Button>
        </Box>
        {status === 'sent' && (
          <Box component="p" role="status" sx={{ m: 0, mt: 2, fontSize: 14, color: hubColors.textSecondary }}>
            {t('dealFinder.returning.sent', { defaultValue: 'If that email is on file, a sign-in link is on its way. Check your inbox.' })}
          </Box>
        )}
        {status === 'throttled' && (
          <Box component="p" role="status" sx={{ m: 0, mt: 2, fontSize: 14, color: hubColors.textSecondary }}>
            {t('dealFinder.returning.throttled', { defaultValue: 'Just sent one. Give it a minute and check your inbox.' })}
          </Box>
        )}
      </Collapse>
    </Box>
  );
};
